<script setup>
  import { computed, onErrorCaptured } from 'vue'
  import { useI18n } from 'vue-i18n';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';

  import UserTipBase from "@/components/base/UserTipBase.vue";
  import Icon from "@/components/icons/Icon.vue";

  import Bugsnag from '@bugsnag/js';

  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const { t } = useI18n();

  const isSmallDevice = useMediaQuery('(max-width: 539px)');

  const props = defineProps({
    menu: {
      type: Boolean,
      default: false
    },
    floating: {
      type: Boolean,
      default: true
    },
    // Passed to UserTipBase: 'centered' | 'left' | 'right' | 'auto'
    alignment: {
      type: String,
      default: 'centered'
    }
  })

  const spotStore = useSpotStore();

  const showMoreMenuIcon = computed(() => isSmallDevice.value && !$WEB_VIEW);

  const menuText = computed(() => {
    return props.menu ? 
      'in the menu ' 
      : $WEB_VIEW
      ? 'next to the title '
      : isSmallDevice.value
      ? 'in the menu '
      : '';
  });

  const styleAdjustments = computed(() => ({
    '--jwx-user-tip-min-width': '22rem',
    '--jwx-user-tip-max-width': '26rem',
    '--jwx-user-tip-offset': '-6px',
    '--jwx-user-tip-close-button-width': '1.25rem',
    // Places the pointer under the button when the tip is left- or right-aligned
    // (a centered tip's pointer ignores it)
    '--jwx-user-tip-pointer-offset': '1.45rem',
    'z-index': '10'
  }))

  onErrorCaptured((e) => {
    console.warn(e);
    Bugsnag.notify(e);

    return false;
  })    
</script>

<template>
  <UserTipBase
    v-if="!spotStore.isSpotFavorited"
    :id="1"
    :floating="props.floating"
    position="bottom"
    :alignment="props.alignment"
    :adjustments="styleAdjustments"
    :style="isSmallDevice ? `margin-top: var(--jwx-block-spacing-sm);` : ''"
  >
    <template #icon>
      <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path d="M0 256a256 256 0 1 1 512 0 256 256 0 1 1-512 0zm256-152c-6.1 0-11.7 3.5-14.3 8.9l-36.2 73.4-81 11.8c-6 .9-11 5.1-12.9 10.9s-.3 12.2 4 16.4l58.6 57.2-13.8 80.7c-1 6 1.4 12.1 6.4 15.6s11.5 4.1 16.8 1.2l72.4-38 72.5 38.1c5.4 2.8 11.9 2.4 16.9-1.2s7.4-9.6 6.4-15.6L338 282.7l58.6-57.2c4.4-4.3 5.9-10.6 4-16.4s-6.9-10-12.9-10.9l-81.1-11.8-36.2-73.4c-2.7-5.5-8.3-8.9-14.3-8.9z"/>
      </svg>
    </template>
    <template #title>
      {{ t('completeReport.favoriteTip.favoriteThisSpot') }}
    </template>
    <template #message> 
      Click the <span class="jwx-v-user-tip__inline-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" role="img">
            <title>{{ t('completeReport.favoriteTip.starIcon') }}</title>
            <path d="M287.9 0c9.2 0 17.6 5.2 21.6 13.5l68.6 141.3 153.2 22.6c9 1.3 16.5 7.6 19.3 16.3s.5 18.1-5.9 24.5L433.6 328.4 459.8 484c1.5 9-2.2 18.1-9.6 23.5s-17.3 6-25.3 1.7l-137-73.2L151 509.1c-8.1 4.3-17.9 3.7-25.3-1.7s-11.2-14.5-9.7-23.5l26.2-155.6L31.1 218.2c-6.5-6.4-8.7-15.9-5.9-24.5s10.3-14.9 19.3-16.3l153.2-22.6 68.6-141.3C270.4 5.2 278.7 0 287.9 0zm0 79-52.5 108.2c-3.5 7.1-10.2 12.1-18.1 13.3L99 217.9l85.9 85.1c5.5 5.5 8.1 13.3 6.8 21l-20.3 119.7 105.2-56.2c7.1-3.8 15.6-3.8 22.6 0l105.2 56.2-20.2-119.6c-1.3-7.7 1.2-15.5 6.8-21l85.9-85.1-118.3-17.5c-7.8-1.2-14.6-6.1-18.1-13.3L287.9 79z"/>
        </svg>
      </span> button {{ menuText }}<span class="jwx-v-user-tip__inline-icon"><Icon v-if="showMoreMenuIcon" name="more-menu" /></span> above to add this spot to your favorites.          
    </template>
  </UserTipBase>
</template>